/**
 * Форматирование строк сравнения и подписей четвёрки (FE-220).
 *
 * Чистые функции без React: их можно проверить отдельно от разметки.
 *
 * **Backend не присылает готовых строк.** Он отдаёт число и единицу либо код
 * справочника; собрать текст — работа этого модуля и i18n (CLAUDE.md §6).
 * Значение, которого нет, превращается в «Нет данных», а не в ноль.
 */

import { formatMileage } from '@entities/vehicle/lib';

import type {
  ComparisonUnit,
  ComparisonValue,
  SelectionExitReason,
  SelectionSlot,
  SelectionState,
} from './types';

/** Ключ i18n подписи параметра сравнения. */
export function comparisonRowKey(code: string): string {
  return `selection.compare.row.${code}`;
}

/** Ключ i18n заголовка группы строк (§34.5). */
export function comparisonGroupKey(code: string): string {
  return `selection.compare.group.${code}`;
}

/** Ключ i18n причины выбытия. Кода без перевода на экране быть не должно. */
export function exitReasonKey(reason: SelectionExitReason): string {
  return `selection.exit.${reason}`;
}

/** Ключ i18n кода tie-break из обоснования отбора. */
export function tieBreakKey(reason: string): string {
  return `selection.basis.tieBreak.${reason}`;
}

/** Ключ i18n географической ступени. */
export function geoStepKey(step: string): string {
  return `selection.basis.geoStep.${step}`;
}

/** Ключ i18n подписи компонента второй оценки (§38.5). */
export function postCheckComponentKey(code: string): string {
  return `selection.postCheck.component.${code}`;
}

/** Значения, которые форматируются как числа и выравниваются по правому краю. */
const NUMERIC_UNITS: ReadonlySet<ComparisonUnit> = new Set<ComparisonUnit>([
  'year',
  'kilometres',
  'kilowatt',
  'currency',
  'litres',
  'kwh',
  'percent',
  'score',
  'count',
  'minutes',
]);

export function isNumericUnit(unit: ComparisonUnit): boolean {
  return NUMERIC_UNITS.has(unit);
}

interface FormatContext {
  locale: string;
  currency: string;
  /** Перевод кода справочника; `null`, если перевода нет. */
  translateCode: (code: string) => string | null;
  /** Перевод шаблона с числом (`selection.compare.unit.*`). */
  translateUnit: (key: string, value: string) => string;
}

/**
 * Значение ячейки в виде готовой строки. `null` — «Нет данных».
 *
 * Отрицательное отклонение от медианы показывается со знаком: «−1 500 €» и
 * «1 500 €» — разные факты, и терять знак нельзя.
 */
export function formatComparisonValue(
  value: ComparisonValue,
  unit: ComparisonUnit,
  context: FormatContext,
): string | null {
  if (value.text !== null) return value.text;
  if (value.code !== null) return context.translateCode(value.code);
  if (value.numeric === null) return null;

  const { locale, currency } = context;
  switch (unit) {
    case 'year':
      return String(Math.round(value.numeric));
    case 'kilometres':
      return context.translateUnit(
        'selection.compare.unit.kilometres',
        formatMileage(Math.round(value.numeric), locale),
      );
    case 'kilowatt':
      return context.translateUnit(
        'selection.compare.unit.kilowatt',
        new Intl.NumberFormat(locale).format(Math.round(value.numeric)),
      );
    case 'currency':
      return formatSignedCurrency(value.numeric, currency, locale);
    case 'litres':
      return context.translateUnit(
        'selection.compare.unit.litres',
        new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(value.numeric),
      );
    case 'kwh':
      return context.translateUnit(
        'selection.compare.unit.kwh',
        new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(value.numeric),
      );
    case 'percent':
      return context.translateUnit(
        'selection.compare.unit.percent',
        new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(value.numeric),
      );
    case 'score':
      return new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(value.numeric);
    case 'count':
      return new Intl.NumberFormat(locale).format(value.numeric);
    // Рабочие минуты, а не календарные (§34.8): подпись обязана это называть,
    // иначе «60 минут» читается как час по часам на стене.
    case 'minutes':
      return context.translateUnit(
        'selection.compare.unit.minutes',
        new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(value.numeric),
      );
    default:
      return null;
  }
}

/**
 * Деньги со знаком: центы приходят целыми, знак несёт смысл.
 *
 * `Intl` сам ставит минус для отрицательных, а для положительных знак не
 * нужен — «+1 500 €» относительно медианы читается как «дороже рынка», и это
 * именно то, что стоит сказать словом в подписи строки, а не плюсом.
 */
function formatSignedCurrency(cents: number, currency: string, locale: string): string {
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(cents / 100);
  } catch {
    return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(cents / 100);
  }
}

/** Автомобиль в четвёрке сейчас (не выбыл). */
export function isLive(slot: SelectionSlot): boolean {
  return slot.status === 'active' || slot.status === 'selected';
}

/**
 * Стадии, на которых четвёрка ещё складывается и слотов может не быть вовсе.
 *
 * `analyzing` — идёт прогон по каталогу (перестройка подбора); `paid` и
 * `selecting` — прогон посчитан, идёт отбор четырёх. Ни одна из них не
 * означает «ничего не подошло»: пустой экран здесь — это ожидание, а не
 * исход. Стадия приходит в самом составе (`SelectionState.stage`), отдельного
 * запроса за ней не делается.
 */
export const BUILDING_STAGES = ['analyzing', 'paid', 'selecting'] as const;

/**
 * Стадии окна замен (§34.5). Зеркало `SELECTION_ACTIONABLE_STAGES` на backend.
 *
 * Именно здесь работает «Не подходит» и подбирается замена. Подтверждение
 * четвёрки («эти четыре подходят») закрывает окно **навсегда для этого
 * прогона**: дальше отказ от автомобиля возможен, но замены не будет.
 * Настоящую проверку делает backend — здесь только доступность кнопок.
 */
export const REPLACEMENT_STAGES = ['top4_ready'] as const;

/**
 * Стадии, на которых автомобиль выбирают (§34.4).
 *
 * Зеркало `SELECTION_CHOICE_STAGES` на backend. Выбор доступен уже на
 * `checking` — по автомобилю, проверка которого закончилась
 * (`check_completed_at`), — и на `deep_check_ready`, когда закончились все.
 * Выбор один на заказ и окончателен: стадии торга `negotiating` и
 * `offer_review` входят ради «Отказаться от этого автомобиля», а не ради
 * второго выбора (`offer_review` — с BE-590: шаг 6 стал основным путём).
 */
export const CHOICE_STAGES = [
  'checking',
  'deep_check_ready',
  'negotiating',
  'offer_review',
] as const;

/** Проверка по автомобилю закончена: его можно выбрать (§36.6). */
export function isCheckComplete(slot: Pick<SelectionSlot, 'check_completed_at'>): boolean {
  return slot.check_completed_at !== null;
}

/** Четвёрка ещё собирается: показывается ожидание, а не пустота и не исход. */
export function isBuilding(state: Pick<SelectionState, 'stage' | 'slots'>): boolean {
  return (
    state.slots.filter(isLive).length === 0 &&
    (BUILDING_STAGES as readonly string[]).includes(state.stage)
  );
}
